/**
 * Rate Limiter Client-Side
 * Protege contra abuso de API e ataques de força bruta
 */

interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
}

class RateLimiter {
  private requests: Map<string, number[]> = new Map();
  
  /**
   * Verifica se uma requisição pode ser feita
   * @param key - Identificador único (ex: userId, endpoint)
   * @param maxRequests - Número máximo de requisições
   * @param windowMs - Janela de tempo em ms
   */
  canMakeRequest(key: string, maxRequests: number, windowMs: number): boolean {
    const now = Date.now();
    const requests = this.requests.get(key) || [];
    
    // Remove requisições antigas fora da janela de tempo
    const validRequests = requests.filter(time => now - time < windowMs);
    
    if (validRequests.length >= maxRequests) {
      return false;
    }
    
    validRequests.push(now);
    this.requests.set(key, validRequests);
    return true;
  }
  
  /**
   * Obtém tempo restante para próxima requisição (em ms)
   */
  getTimeUntilNextRequest(key: string, windowMs: number): number {
    const requests = this.requests.get(key);
    if (!requests || requests.length === 0) return 0;
    
    const oldest = Math.min(...requests);
    const timePassed = Date.now() - oldest;
    const timeRemaining = windowMs - timePassed;
    
    return Math.max(0, timeRemaining);
  }
  
  /**
   * Limpa histórico de requisições para uma chave
   */
  clear(key: string): void {
    this.requests.delete(key);
  }
  
  /**
   * Limpa todo o histórico
   */
  clearAll(): void {
    this.requests.clear();
  }
}

export const rateLimiter = new RateLimiter();

/**
 * Wrapper HOF para aplicar rate limiting em funções assíncronas
 */
export async function withRateLimit<T>(
  key: string,
  fn: () => Promise<T>,
  config: RateLimitConfig = { maxRequests: 10, windowMs: 10000 }
): Promise<T> {
  const { maxRequests, windowMs } = config;
  
  if (!rateLimiter.canMakeRequest(key, maxRequests, windowMs)) {
    const waitTime = rateLimiter.getTimeUntilNextRequest(key, windowMs);
    throw new Error(
      `Muitas requisições. Aguarde ${Math.ceil(waitTime / 1000)}s e tente novamente.`
    );
  }
  
  return fn();
}

/**
 * Configurações de rate limit pré-definidas
 */
export const RateLimitPresets = {
  // Autenticação (login, signup) - muito restritivo
  AUTH: { maxRequests: 5, windowMs: 60000 }, // 5 req/min
  
  // Operações críticas (bulk delete, etc)
  CRITICAL: { maxRequests: 3, windowMs: 60000 }, // 3 req/min
  
  // Operações normais (CRUD)
  NORMAL: { maxRequests: 30, windowMs: 60000 }, // 30 req/min
  
  // Leitura de dados
  READ: { maxRequests: 100, windowMs: 60000 }, // 100 req/min
  
  // Rate limit agressivo para prevenir spam
  SPAM_PROTECTION: { maxRequests: 1, windowMs: 5000 }, // 1 req/5s
};

/**
 * Hook para usar rate limiter em componentes React
 */
export function useRateLimiter(key: string, config: RateLimitConfig) {
  return {
    canMakeRequest: () => rateLimiter.canMakeRequest(key, config.maxRequests, config.windowMs),
    getTimeUntilNext: () => rateLimiter.getTimeUntilNextRequest(key, config.windowMs),
    clear: () => rateLimiter.clear(key),
  };
}

/**
 * Decorator pattern para rate limiting
 */
export function rateLimit(config: RateLimitConfig) {
  return function (
    target: any,
    propertyName: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;
    
    descriptor.value = async function (...args: any[]) {
      const key = `${target.constructor.name}.${propertyName}`;
      return withRateLimit(key, () => originalMethod.apply(this, args), config);
    };
    
    return descriptor;
  };
}
